import { ChangeEvent, FC, useEffect, useState } from 'react';
import Button from '../../Button';
import Radio from '../../Radio';
import { IParent, IStudent } from '../../../models/students.model';
import useAxios from '../../../hooks/useAxios';
import { IGetAuth } from '../../../models/auth.model';
import { switchAuth } from '../../../api/auth.api';
import ErrorResponse from '../../ErrorResponse';
import SearchPostcodeModal from '../../modals/SearchPostcodeModal';
import ResetParentPasswordModal from './modals/ResetParentPasswordModal';
import useNotification from '../../../hooks/useNotification';
import { Descriptions } from 'antd';
import _ from 'lodash';
import { updateParent } from '../../../api/students.api';
import useAuth from '../../../hooks/useAuth';
import Loader from '../../Loader';
import { unlockAccount } from '../../../api/employees.api';
import { useTranslation } from 'react-i18next';
import { addHyphen, getWebUrl } from '../../../lib/util';

interface IProps {
  isEdit: boolean;
  student: IStudent | undefined;
  parent: IParent | undefined;

  onEditClick(): void;
}

const ParentInfoEditor: FC<IProps> = ({ isEdit, parent, onEditClick }) => {
  const { writeAuthYn } = useAuth();
  const { t } = useTranslation();
  const { successNotification, infoNotification } = useNotification();
  const {
    data: auth,
    isLoading: authIsLoading,
    error: authError,
    execute: authExecute,
  } = useAxios<IGetAuth>();
  const {
    data: updateParentResult,
    isLoading: updateParentIsLoading,
    error: updateParentError,
    execute: updateParentExecute,
  } = useAxios();
  const {
    data: unlockRes,
    isLoading: unlockIsLoading,
    error: unlockError,
    execute: unlockExecute,
  } = useAxios();

  const [postcodeVisible, setPostcodeVisible] = useState(false);
  const [resetPasswordVisible, setResetPasswordVisible] = useState(false);
  const [inputs, setInputs] = useState<any>({});

  useEffect(() => {
    if (parent) {
      setInputs(_.cloneDeep(parent));
    }
  }, [parent]);

  useEffect(() => {
    if (auth && !authIsLoading) {
      window.location.href = getWebUrl();
    }
  }, [auth, authIsLoading]);

  useEffect(() => {
    if (updateParentResult && !updateParentIsLoading) {
      successNotification(t('student.student.message1'));
      // TODO
      // if (student) getParentExecute(getParent(student.studentCode));
    }
  }, [updateParentResult, updateParentIsLoading]);

  useEffect(() => {
    if (unlockRes && !unlockIsLoading) {
      successNotification(t('student.student.message2'));
    }
  }, [unlockRes, unlockIsLoading]);

  const onParentInputChange = (
    { target: { value } }: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    key: any,
  ) => {
    const newInputs: any = { ...inputs };
    (newInputs ?? {})[key] = value;
    setInputs(newInputs);
  };

  const onParentGenderChange = (value: 'M' | 'F') => {
    setInputs({ ...inputs, genderDiv: value });
  };

  const onParentSMSChange = (value: 'Y' | 'N') => {
    setInputs({ ...inputs, additionalParentSmsYn: value });
  };

  const onConsentChange = (value: 'Y' | 'N') => {
    setInputs({ ...inputs, consentYn: value });
  };

  const onLocalEditClick = () => {
    if (!parent?.memberCode) return;

    if (isEdit) {
      if (!inputs?.memberKorName)
        return infoNotification(t('student.student.message3'));
      if (!inputs?.mobilePhone)
        return infoNotification(t('student.student.message10'));
      if (!inputs?.detailAddress)
        return infoNotification(t('student.student.message11'));

      updateParentExecute(updateParent(parent.memberCode, inputs));
    }
    onEditClick();
  };

  const onSwitchAuth = (memberCode: string) => {
    if (!memberCode) return;
    authExecute(switchAuth(memberCode));
  };

  const onCompletePostcode = ({ jibunAddress, zonecode }: any) => {
    setInputs({ ...inputs, address: jibunAddress, zipNo: zonecode });
    setPostcodeVisible(false);
  };

  const onUnlockAccount = (memberCode?: string) => {
    if (!memberCode) return;
    unlockExecute(unlockAccount(memberCode));
  };

  return (
    <>
      {authIsLoading || updateParentIsLoading || unlockIsLoading ? (
        <Loader />
      ) : null}
      <ErrorResponse error={authError} />
      <ErrorResponse error={updateParentError} />
      <ErrorResponse error={unlockError} />
      <div className="comm-bx__space">
        <h3 className='sub-title'>{t('student.student.text3')}</h3>
      </div>

      <div className='comm-table__area rowType'>
        <Descriptions bordered size="small" column={2}>
          <Descriptions.Item
            label={t('student.student.column5') + (isEdit ? '' : '(ID)')}
          >
            <div className="d-flex align-items-center">
              <input
                type="text"
                className="form-control"
                value={
                  isEdit
                    ? inputs?.memberKorName
                    : `${inputs?.memberKorName} (${inputs?.webId})`
                }
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  onParentInputChange(e, 'memberKorName')
                }
                disabled={!isEdit}
              />
              {!isEdit && (
                <div className="comm-btn__area">
                  <button
                    className="btn-basic small mint"
                    onClick={() => onSwitchAuth(inputs?.memberCode)}
                  >
                    {t('auth.login.title')}
                  </button>
                  {inputs?.guestJoinYn === 'Y' && (
                    <button
                      className="btn-basic small mint"
                      onClick={() => setResetPasswordVisible(true)}
                    >
                      {t('auth.login.label2')}
                    </button>
                  )}
                  {writeAuthYn === 'Y' ? (
                    <button
                      className="btn-basic small mint"
                      onClick={() => onUnlockAccount(inputs?.memberCode)}
                    >
                      {t('student.student.button2')}
                    </button>
                  ) : null}
                </div>
              )}
            </div>
          </Descriptions.Item>
          <Descriptions.Item label={t('student.student.column9')}>
            <input
              type="text"
              className="form-control input-text"
              value={
                isEdit ? inputs?.mobilePhone : addHyphen(inputs?.mobilePhone)
              }
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onParentInputChange(e, 'mobilePhone')
              }
              disabled={!isEdit}
            />
          </Descriptions.Item>
          <Descriptions.Item label={t('student.student.column10')} span={2}>
            <Radio
              checked={inputs?.genderDiv?.trim() === 'M'}
              onChange={() => onParentGenderChange('M')}
              text={t('student.student.option4')}
              disabled={!isEdit}
            />
            <Radio
              className="ml-3"
              checked={inputs?.genderDiv?.trim() === 'F'}
              onChange={() => onParentGenderChange('F')}
              text={t('student.student.option5')}
              disabled={!isEdit}
            />
          </Descriptions.Item>
          <Descriptions.Item label={t('common.text.address1')} span={2}>
            <div className="comm-btn__area">
              {isEdit && (
                <>                
                  <Button
                    className="btn-basic black"
                    onClick={() => setPostcodeVisible(true)}
                    text={t('student.student.button11')}
                  />{' '}
                </>
              )}
              <span className='v-line'>{inputs?.zipNo ? `(${inputs.zipNo})` : ''} {inputs?.address}</span>
            </div>
          </Descriptions.Item>
          <Descriptions.Item label={t('common.text.address2')} span={2}>
            <input
              type="text"
              className="form-control input-text"
              value={inputs?.detailAddress}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onParentInputChange(e, 'detailAddress')
              }
              disabled={!isEdit}
            />
          </Descriptions.Item>
          <Descriptions.Item label={t('student.student.column13')}>
            <input
              type="text"
              className="form-control input-text"
              value={inputs?.email}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onParentInputChange(e, 'email')
              }
              disabled={!isEdit}
            />
          </Descriptions.Item>
          <Descriptions.Item label={t('student.student.column14')}>
            <Radio
              checked={inputs?.consentYn === 'Y'}
              onChange={() => onConsentChange('Y')}
              text={t('common.option.yes')}
              disabled
            />
            <Radio
              className="ml-3"
              checked={inputs?.consentYn === 'N'}
              onChange={() => onConsentChange('N')}
              text={t('common.option.no')}
              disabled
            />
          </Descriptions.Item>
          <Descriptions.Item label={t('student.student.column15')}>
            <input
              type="text"
              className="form-control input-text"
              value={inputs?.additionalParentName}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onParentInputChange(e, 'additionalParentName')
              }
              disabled={!isEdit}
            />
          </Descriptions.Item>
          <Descriptions.Item label={t('student.student.column16')}>
            <input
              type="text"
              className="form-control input-text"
              value={
                isEdit
                  ? inputs?.additionalParentTelephone
                  : addHyphen(inputs?.additionalParentTelephone)
              }
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onParentInputChange(e, 'additionalParentTelephone')
              }
              disabled={!isEdit}
            />
          </Descriptions.Item>
          <Descriptions.Item label={t('student.student.column17')} span={2}>
            <Radio
              checked={inputs?.additionalParentSmsYn === 'N'}
              onChange={() => onParentSMSChange('N')}
              text={inputs?.memberKorName ?? ''}
              disabled={!isEdit}
            />
            <Radio
              className="ml-3"
              checked={inputs?.additionalParentSmsYn === 'Y'}
              onChange={() => onParentSMSChange('Y')}
              text={
                inputs?.additionalParentName ||
                `(${t('student.student.column11')})`
              }
              disabled={!isEdit}
            />
          </Descriptions.Item>
          <Descriptions.Item label={t('student.student.column12')} span={2}>
            <textarea
              className="w-100"
              rows={3}
              value={inputs?.memo}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                onParentInputChange(e, 'memo');
              }}
              disabled={!isEdit}
            />
          </Descriptions.Item>
        </Descriptions>
      </div>

      <div className="comm-bx__space">
        <div className="comm-btn__area right">
          <Button
            className={isEdit ? "btn-basic pink" : "btn-basic pink-line"}
            onClick={onLocalEditClick}
            text={isEdit ? t('common.button.save') : t('common.button.edit')}
            writeAuthYn={writeAuthYn}
          />
        </div>
      </div>
      <SearchPostcodeModal
        postcodeVisible={postcodeVisible}
        setPostcodeVisible={setPostcodeVisible}
        onCompletePostcode={onCompletePostcode}
      />
      <ResetParentPasswordModal
        visible={resetPasswordVisible}
        setVisible={setResetPasswordVisible}
        parentCode={parent?.memberCode}
      />
    </>
  );
};

export default ParentInfoEditor;
